/**
 * Copy and question set for the Black Friday readiness checklist.
 *
 * English-only by design: the tool lives under /tools, which ships to the
 * English locales only (see EN_ONLY_SLUGS). There is no locale-varying piece
 * here (no currency), so everything is a plain string.
 */

/** Optional cross-link to another toolkit tool, shown under a question. */
export interface QuestionLink {
  /** Link label (an arrow prefix is added in the UI). */
  text: string;
  /** Internal path to the linked tool. */
  href: string;
}

export interface ChecklistQuestion {
  /** The yes/no question text. */
  text: string;
  /**
   * Optional nudge to a related tool. Shown while the question is unanswered
   * or answered "no", and hidden once the user answers "yes".
   */
  link?: QuestionLink;
}

export interface ChecklistCategory {
  /** Short, uppercase-in-UI stage name (e.g. "Offer"). */
  name: string;
  /** Yes/no preparation questions for the stage. */
  questions: ChecklistQuestion[];
}

export const STRINGS = {
  toolTitle: "Your preparation",
  resultPrompt: "Answer the questions to see how ready you are.",
  startHere: "Start here",
  disclaimer:
    "Based on common BFCM preparation guides. Every brand runs a different playbook, so treat this as a starting point rather than a complete plan.",
} as const;

export interface FaqQuestion {
  question: string;
  answer: string;
}

export const FAQ_TITLE = "Readiness checklist FAQ";

export const FAQ_QUESTIONS: FaqQuestion[] = [
  {
    question: "What is a Black Friday readiness checklist?",
    answer:
      "A Black Friday readiness checklist is a structured set of yes/no questions covering the areas that most commonly go wrong during BFCM - offer, inventory, site, email, ads and fulfilment - so you can see at a glance what's covered and what's still open. This tool runs 20 such questions across those six areas and scores your answers as you go.",
  },
  {
    question: "When should you start preparing for Black Friday?",
    answer:
      "There's no single date, but earlier is almost always better: inventory needs supplier lead times factored in weeks or months out, while site testing, email sequences and ad creative can usually be locked down in the final few weeks. If you're starting late, work through the categories in order and prioritise whichever one this checklist flags as your biggest gap - or use the [backwards planner](/tools/backwards-planner) to map every task back from sale day.",
  },
  {
    question: "How is my readiness score calculated?",
    answer:
      'Each "yes" counts toward your total out of 20, tracked per category as you go. Once every question is answered, the tool flags the category with the lowest share of "yes" answers as **Start here** - the area with the most ground to make up before the sale.',
  },
  {
    question: "Do I need to answer every question to see a result?",
    answer:
      "No - the running score updates after every answer. The **Start here** flag only appears once all 20 are answered, since it needs the full picture to point at your biggest gap rather than a partial one.",
  },
  {
    question: "Is my checklist data saved anywhere?",
    answer:
      "No. Your answers stay in the browser tab and are never sent anywhere or stored. Once every question is answered, we log the aggregate score (e.g. 14 of 20) for our own product analytics - individual answers are never included.",
  },
  {
    question: 'What if most of my answers are still "no"?',
    answer:
      "It's rarely too late to close every gap, but some take longer than others - a site load test needs more lead time than writing an early-access email. Start with the flagged category, then work through what's realistic in the time you have left. A partially-prepared plan beats no plan.",
  },
  {
    question: "Where can I get help fixing specific gaps?",
    answer:
      "Several questions link straight to a purpose-built calculator - the [discount break-even calculator](/tools/discount-break-even) for margin maths, the [order quantity calculator](/tools/order-quantity-calculator) for stock forecasts, and more in the [full Black Friday toolkit](/tools).",
  },
];

export const CATEGORIES: ChecklistCategory[] = [
  {
    name: "Offer",
    questions: [
      {
        text: "Have you decided your headline offer - discount depth, bundle or gift with purchase?",
      },
      {
        text: "Have you checked the margin maths on every SKU you plan to discount?",
        link: {
          text: "Check this with the discount break-even calculator",
          href: "/tools/discount-break-even",
        },
      },
      {
        text: "Have you set a margin floor you won't discount past, whatever competitors do?",
      },
      {
        text: "Do you have an early-access or VIP offer for your best customers?",
      },
    ],
  },
  {
    name: "Inventory",
    questions: [
      {
        text: "Have you forecast demand for your hero SKUs using last year's numbers?",
        link: {
          text: "Check this with the order quantity calculator",
          href: "/tools/order-quantity-calculator",
        },
      },
      {
        text: "Have you placed stock orders with supplier lead times factored in?",
      },
      {
        text: "Do you have a plan for what happens if a hero SKU sells out mid-sale?",
      },
      {
        text: "Is your stock count accurate and synced across every sales channel?",
      },
    ],
  },
  {
    name: "Site",
    questions: [
      {
        text: "Has your site been load-tested at several times your normal peak traffic?",
      },
      {
        text: "Have you tested your mobile checkout end to end, including express payment options?",
      },
      {
        text: "Are your BFCM landing pages and banners built and ready to switch on?",
      },
      {
        text: "Have you set a code-freeze date after which nothing on the site changes?",
      },
    ],
  },
  {
    name: "Email",
    questions: [
      {
        text: "Is your list segmented - VIPs, last year's BFCM buyers, lapsed customers?",
      },
      {
        text: "Is your full sequence written and scheduled, from teaser to last chance?",
      },
      {
        text: "Have you kept your sending domain warm with regular sends through autumn?",
      },
    ],
  },
  {
    name: "Ads",
    questions: [
      { text: "Are your BFCM creatives produced, approved and uploaded?" },
      { text: "Have you set budgets and target ROAS for the sale period?" },
      {
        text: "Have you started warming up audiences with prospecting campaigns?",
      },
    ],
  },
  {
    name: "Fulfilment",
    questions: [
      {
        text: "Have you confirmed capacity and cut-off dates with your 3PL or courier?",
      },
      {
        text: "Are you stocked up on packaging and staffed up for support and returns?",
      },
    ],
  },
];
